/**
 * Small page-level dialogs of the client shell, drawn over the canvas in the colours of the game's popups:
 *  - the exit question of the Android back button on the main map;
 *  - the server form of the loading screen, for an app whose server does not answer or is not set yet (the sign-in
 *    screen's own "Server" view needs a connected game, and this one does not). A real <input>, so the phone's own
 *    keyboard, paste and autofill work; its Enter ("go") sends the form. Over an on-screen keyboard (the page keeps
 *    its size: setDialogKeyboard) it stays in the part of the screen above the keyboard, compact.
 * Texts come translated from the caller.
 */

const CSS = `
.fwp-dialog{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:10;background:rgba(0,0,0,.45);
  padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)}
.fwp-dialog form{background:#f3e3b5;border:3px solid #5b3a12;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.6);
  padding:16px 18px;width:min(360px,calc(100vw - 32px));box-sizing:border-box;font:14px Arial,sans-serif;color:#3b2508;text-align:center}
.fwp-dialog h2{font-size:17px;margin:0 0 12px}
.fwp-dialog p{margin:6px 0;font-size:12px;color:#6b5330;text-align:left}
.fwp-dialog p.error{color:#b00000;font-weight:bold;text-align:center}
.fwp-dialog input{width:100%;box-sizing:border-box;font:16px Arial,sans-serif;padding:10px;border:2px solid #000;border-radius:4px;background:#fff}
.fwp-dialog .row{display:flex;gap:10px;justify-content:center;margin-top:14px;flex-wrap:wrap}
.fwp-dialog button{min-width:96px;min-height:44px;font:bold 15px Arial,sans-serif;border-radius:8px;border:2px solid #3b5e0c;
  background:#8fc23a;color:#fff;text-shadow:0 1px 1px #264000;padding:0 14px}
.fwp-dialog button.plain{background:#e9d49a;color:#3b2508;border-color:#8a6a2c;text-shadow:none}
/* An on-screen keyboard (setDialogKeyboard): the dialog in the part of the screen above it, compact; what does not
   fit scrolls. */
.fwp-dialog.kb{bottom:var(--fwp-kb,0px);align-items:center;align-items:safe center;overflow:auto;padding-top:4px;padding-bottom:4px}
.fwp-dialog.kb form{padding:10px 14px}
.fwp-dialog.kb h2{font-size:15px;margin:0 0 6px}
.fwp-dialog.kb p.label,.fwp-dialog.kb p.hint{display:none}
.fwp-dialog.kb .row{margin-top:8px}
.fwp-status-button{margin-top:18px;z-index:4;min-height:44px;min-width:120px;font:bold 15px Arial,sans-serif;border-radius:8px;border:2px solid #8a6a2c;
  background:#e9d49a;color:#3b2508;pointer-events:auto;flex:none}
`;

function ensureStyle(doc: Document): void {
  if (doc.getElementById('fwp-dialog-style')) return;
  const style = doc.createElement('style');
  style.id = 'fwp-dialog-style';
  style.textContent = CSS;
  doc.head.appendChild(style);
}

function el<K extends keyof HTMLElementTagNameMap>(doc: Document, tag: K, props: Record<string, any> = {}, text?: string): HTMLElementTagNameMap[K] {
  const e = doc.createElement(tag);
  Object.assign(e, props);
  if (text !== undefined) e.textContent = text;
  return e;
}

/**
 * The root of a dialog: a modal one (role dialog, aria-modal): while one of its controls has the focus, the game's
 * hidden text input does not take it back (src/flash/platform/browser-input.ts syncTextFocus), so a dialog over the
 * sign-in screen gets its keys.
 */
function dialogRoot(doc: Document, id: string): HTMLDivElement {
  const root = el(doc, 'div', { className: 'fwp-dialog', id });
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  return root;
}

/** The open dialog of this page (one at a time). */
let open: HTMLElement | null = null;
/** The on-screen keyboard's height over the page's bottom (CSS px; 0: none): setDialogKeyboard. */
let keyboard = 0;

/**
 * The on-screen keyboard's height over the page's bottom (CSS px; 0: none: src/client/platform/viewport.ts): the open
 * dialog and the next ones stay in the part of the screen above it (the page is not resized for the keyboard).
 */
export function setDialogKeyboard(px: number): void {
  keyboard = Number.isFinite(px) && px > 0 ? Math.round(px) : 0;
  if (open) fitKeyboard(open);
}

function fitKeyboard(root: HTMLElement): void {
  root.classList.toggle('kb', keyboard > 0);
  root.style.setProperty('--fwp-kb', `${keyboard}px`);
  // The focused field in view above the keyboard.
  const f = root.ownerDocument.activeElement as HTMLElement | null;
  if (keyboard > 0 && f && root.contains(f) && typeof f.scrollIntoView === 'function') f.scrollIntoView({ block: 'nearest' });
}

export function dialogOpen(): boolean {
  return !!open && open.isConnected;
}

export function closeDialog(): void {
  open?.remove();
  open = null;
}

/**
 * A yes / no question; resolves with the answer (false when another dialog replaces it). While it is open the keys
 * of a physical keyboard are the dialog's: none reaches the game (which listens on the window), Escape answers "no",
 * Enter / Space press the focused button.
 */
export function confirmDialog(texts: { question: string; yes: string; no: string }, doc: Document = document): Promise<boolean> {
  ensureStyle(doc);
  closeDialog();
  return new Promise((resolve) => {
    const win = doc.defaultView;
    const held = new Set<string>();
    let released = false;
    const stopKeyups = () => win?.removeEventListener('keyup', onKeyUp, { capture: true });
    // Capture phase on the window: before the game's listeners; the default action (a button's click) still runs.
    const onKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      // An auto-repeat is no new press: a key held since before the dialog repeats while it is open, and its keyup is
      // still the game's.
      if (!e.repeat) held.add(e.code || e.key);
      if (e.key === 'Escape') {
        e.preventDefault();
        done(false);
      }
    };
    // Only the keyups of keys pressed while the dialog was open: a key held since before (the game saw it go down)
    // must reach the game when it is released, or the game would keep it held.
    const onKeyUp = (e: KeyboardEvent) => {
      if (!held.delete(e.code || e.key)) return;
      e.stopPropagation();
      if (released && held.size === 0) stopKeyups();
    };
    win?.addEventListener('keydown', onKeyDown, { capture: true });
    win?.addEventListener('keyup', onKeyUp, { capture: true });
    const release = () => {
      if (released) return;
      released = true;
      win?.removeEventListener('keydown', onKeyDown, { capture: true });
      // The key that answered (Enter, Escape) is still the dialog's until it is released.
      if (held.size === 0) stopKeyups();
      else setTimeout(stopKeyups, 3_000);
    };
    const root = dialogRoot(doc, 'fwp-confirm');
    const form = el(doc, 'form');
    form.append(el(doc, 'h2', {}, texts.question));
    const row = el(doc, 'div', { className: 'row' });
    const yes = el(doc, 'button', { type: 'submit', name: 'yes' }, texts.yes);
    const no = el(doc, 'button', { type: 'button', name: 'no', className: 'plain' }, texts.no);
    row.append(no, yes);
    form.append(row);
    root.append(form);
    const done = (answer: boolean) => {
      if (open === root) closeDialog();
      release();
      resolve(answer);
    };
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      done(true);
    });
    no.addEventListener('click', () => done(false));
    root.addEventListener('pointerdown', (e) => {
      if (e.target === root) done(false);
    });
    new MutationObserver((_, obs) => {
      if (!root.isConnected) {
        obs.disconnect();
        release();
        resolve(false);
      }
    }).observe(doc.body, { childList: true });
    doc.body.appendChild(root);
    open = root;
    no.focus({ preventScroll: true });
  });
}

export interface ServerFormTexts {
  title: string;
  label: string;
  hint: string;
  save: string;
  reset: string;
  cancel: string;
}

/**
 * The server form: `check` returns the text of a refusal or null; `apply` stores the address ('' = the default) and
 * restarts. `cancel` null hides the cancel button (nothing to go back to).
 */
export function serverDialog(
  texts: ServerFormTexts,
  value: string,
  check: (address: string) => string | null,
  apply: (address: string) => void,
  opts: { cancel?: boolean; error?: string | null } = {},
  doc: Document = document,
): HTMLElement {
  ensureStyle(doc);
  closeDialog();
  const root = dialogRoot(doc, 'fwp-server');
  const form = el(doc, 'form');
  form.append(el(doc, 'h2', {}, texts.title));
  // A text input with the URL keyboard: type=url would refuse a bare host:port (the browser's form validation).
  form.noValidate = true;
  const input = el(doc, 'input', { type: 'text', name: 'server', value, autocomplete: 'url', spellcheck: false });
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('autocorrect', 'off');
  input.setAttribute('enterkeyhint', 'go');
  input.setAttribute('inputmode', 'url');
  input.setAttribute('aria-label', texts.label);
  const error = el(doc, 'p', { className: 'error' }, opts.error ?? '');
  form.append(el(doc, 'p', { className: 'label' }, texts.label), input, error, el(doc, 'p', { className: 'hint' }, texts.hint));
  const row = el(doc, 'div', { className: 'row' });
  const save = el(doc, 'button', { type: 'submit', name: 'save' }, texts.save);
  const reset = el(doc, 'button', { type: 'button', name: 'reset', className: 'plain' }, texts.reset);
  row.append(reset, save);
  if (opts.cancel !== false) {
    const cancel = el(doc, 'button', { type: 'button', name: 'cancel', className: 'plain' }, texts.cancel);
    cancel.addEventListener('click', () => closeDialog());
    row.append(cancel);
  }
  form.append(row);
  root.append(form);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const address = input.value.trim();
    const problem = check(address);
    error.textContent = problem ?? '';
    if (problem) {
      input.focus();
      return;
    }
    input.blur();
    apply(address);
  });
  reset.addEventListener('click', () => apply(''));
  // Keys typed here are the form's, not the game's (the canvas listens on the window).
  for (const type of ['keydown', 'keyup', 'pointerdown']) root.addEventListener(type, (e) => e.stopPropagation());
  doc.body.appendChild(root);
  open = root;
  fitKeyboard(root);
  input.addEventListener('focus', () => fitKeyboard(root));
  return root;
}

/** A message with one button (e.g. the result of an account link the app was opened with). */
export function noticeDialog(texts: { title: string; text: string; ok: string }, doc: Document = document): Promise<void> {
  ensureStyle(doc);
  closeDialog();
  return new Promise((resolve) => {
    const root = dialogRoot(doc, 'fwp-notice');
    const form = el(doc, 'form');
    form.append(el(doc, 'h2', {}, texts.title), el(doc, 'p', {}, texts.text));
    const row = el(doc, 'div', { className: 'row' });
    row.append(el(doc, 'button', { type: 'submit', name: 'ok' }, texts.ok));
    form.append(row);
    root.append(form);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (open === root) closeDialog();
      resolve();
    });
    for (const type of ['keydown', 'keyup', 'pointerdown']) root.addEventListener(type, (e) => e.stopPropagation());
    doc.body.appendChild(root);
    open = root;
    (form.querySelector('button') as HTMLButtonElement).focus({ preventScroll: true });
  });
}

export interface PasswordFormTexts {
  title: string;
  label: string;
  /** The label of the second field (the password once more). */
  repeat: string;
  /** The refusal when the two fields differ. */
  mismatch: string;
  hint: string;
  save: string;
  cancel: string;
  wait: string;
}

/**
 * The new password form of a password reset link the app was opened with: `submit` sends it and resolves with the text
 * of a refusal or null (done: the form closes). Real <input> fields (the phone's keyboard and password managers); the
 * password goes from the field into `submit` only and the fields are emptied at once. It is typed twice: a typo would
 * end every session of the account with a password nobody knows (the link page asks the same).
 */
export function passwordDialog(texts: PasswordFormTexts, submit: (password: string) => Promise<string | null>, doc: Document = document): HTMLElement {
  ensureStyle(doc);
  closeDialog();
  const root = dialogRoot(doc, 'fwp-password');
  const form = el(doc, 'form');
  form.noValidate = true;
  form.append(el(doc, 'h2', {}, texts.title));
  const field = (name: string, label: string, enter: string) => {
    // The label is also the placeholder: over an on-screen keyboard the labels are hidden (.kb).
    const f = el(doc, 'input', { type: 'password', name, autocomplete: 'new-password', maxLength: 128, placeholder: label });
    f.setAttribute('enterkeyhint', enter);
    f.setAttribute('aria-label', label);
    return f;
  };
  const input = field('password', texts.label, 'next');
  const repeat = field('repeat', texts.repeat, 'done');
  const fields = [input, repeat];
  const error = el(doc, 'p', { className: 'error' }, '');
  form.append(el(doc, 'p', { className: 'label' }, texts.label), input, el(doc, 'p', { className: 'label' }, texts.repeat), repeat, error, el(doc, 'p', { className: 'hint' }, texts.hint));
  // Enter in the first field goes on to the second while that one is empty (it would send the form otherwise).
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && repeat.value === '') {
      e.preventDefault();
      repeat.focus();
    }
  });
  const row = el(doc, 'div', { className: 'row' });
  const save = el(doc, 'button', { type: 'submit', name: 'save' }, texts.save);
  const cancel = el(doc, 'button', { type: 'button', name: 'cancel', className: 'plain' }, texts.cancel);
  row.append(cancel, save);
  form.append(row);
  root.append(form);
  let busy = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy) return;
    if (input.value !== repeat.value) {
      repeat.value = '';
      error.textContent = texts.mismatch;
      repeat.focus();
      return;
    }
    const password = input.value;
    input.value = repeat.value = '';
    busy = true;
    save.disabled = cancel.disabled = input.disabled = repeat.disabled = true;
    save.textContent = texts.wait;
    void submit(password).then((problem) => {
      busy = false;
      save.disabled = cancel.disabled = input.disabled = repeat.disabled = false;
      save.textContent = texts.save;
      if (problem === null) {
        if (open === root) closeDialog();
        return;
      }
      error.textContent = problem;
      input.focus();
    });
  });
  cancel.addEventListener('click', () => {
    if (!busy) closeDialog();
  });
  for (const type of ['keydown', 'keyup', 'pointerdown']) root.addEventListener(type, (e) => e.stopPropagation());
  doc.body.appendChild(root);
  open = root;
  fitKeyboard(root);
  for (const f of fields) f.addEventListener('focus', () => fitKeyboard(root));
  input.focus({ preventScroll: true });
  return root;
}

/** A button under the loading screen's status line (e.g. "Server"), or null to remove it. */
export function statusButton(label: string | null, onClick: () => void, doc: Document = document): void {
  ensureStyle(doc);
  const status = doc.getElementById('status');
  let b = doc.getElementById('fwp-status-button') as HTMLButtonElement | null;
  if (!label || !status) {
    b?.remove();
    return;
  }
  if (!b) {
    b = el(doc, 'button', { id: 'fwp-status-button', className: 'fwp-status-button', type: 'button' });
    b.addEventListener('pointerdown', (e) => e.stopPropagation());
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    status.after(b);
  }
  b.textContent = label;
}
